<template>
<!-- infinite-scroll-distance="800" – roughly 2.5 card heights. TODO conditionally change depending on how many cards
there are in a row so a fixed amount of overhead products is maintained? -->
<div v-async-infinite-scroll="() => $emit('reaching-scroll-end')"
     infinite-scroll-distance="800"
     infinite-scroll-listen-for-event="_products-updated"
     infinite-scroll-disabled="dontWatchScrollEnd"
     class="products-list-component"
>
  <div class="products-list" :class="productsListClasses">
    <product-card v-for="product in products"
                  :key="product.id"
                  :source="source"
                  :product="product"
                  :category="category"
                  :style-type="cardsStyleType"
                  :promotion-id="promotionId"
                  :reward-id="rewardId"
                  class="products-list-item"
    />
    <!-- Hacky stub cards to avoid the effect when cards in the last row are wider than the cards in the
            previous rows if the card count is different in them (e.g. the last row has 2 cards, the other rows have 5).
            -->
    <div
      v-for="(_, i) in Array(stubCardCount)"
      class="products-list-item products-list-item--stub"
      aria-hidden="true"
    />
  </div>
  <ProductPage v-if="sidebarProductId" :product-id="sidebarProductId"/>
</div>
</template>

<script>
import ProductCard from './ProductCard'
import { mapGetters } from 'vuex'
import HasSectionHighlights from '@/mixins/HasSectionHighlights'

export default {
  directives: {
    asyncInfiniteScroll: {
      async bind(...args) {
        const directive = (await import(/* webpackPreload: true */ 'vue-infinite-scroll')).default;
        directive.bind(...args);
        const vnode = args[2];
        // A hack. Because the directive internally relies on the `mounted` lifecycle hook, but it may be called before
        // the directive is bound so it would never get properly initialized otherwise.
        // If this causes trouble, consider reverting the commit that introduced this change.
        vnode.context.$emit('hook:mounted');

        if (process.env.NODE_ENV !== 'production') {
          if (Object.keys(directive).some(k => !['bind', 'unbind', 'install'].includes(k))) {
            throw new Error("The `infinite-scroll` directive uses hooks other than 'bind' & 'unbind' (which was the "
              + "case at the time this was written), but they are not implemented in this custom version.")
          }
        }
      },
      async unbind(...args) {
        (await import('vue-infinite-scroll')).default.unbind(...args);
      },
    }
  },

  mixins: [HasSectionHighlights],

  props: {
    source: {
      required: true,
      type: String,
    },

    products: {},

    category: {},

    sidebarProductId: {
      required: false,
      type: String,
    },
    /**
     * If false, the component will not be emitting 'reaching-scroll-end' even if scroll end is reached.
     */
    watchScrollEnd: {
      type: Boolean,
      default: false,
    },

    promotionId: {
      required: false,
      type: String,
      default: null,
    },

    rewardId: {
      required: false,
      type: String,
      default: null,
    },
  },

  components: {
    ProductCard,
    ProductPage: () => import('@/pages/ProductPage'),
  },

  computed: {
    ...mapGetters({
      productsSettings: 'settings/products',
    }),

    dontWatchScrollEnd() {
      return !this.watchScrollEnd
    },

    cardsStyleType() {
      return 'frameless';
    },

    productsListClasses() {
      const classes = [`cards-style-type-${this.cardsStyleType}`]

      if (this.productsSettings.cards_count_desktop === 3) {
        classes.push('cards-wide')
      }

      if (this.sectionHighlightClasses) {
        classes.push(this.sectionHighlightClasses)
      }

      return classes
    },

    // Does not return the very precise value we need for this, but roughly what we need + a couple of redundant,
    // to be safe.
    // TODO make reactive to screen width changes? Or would that be too much for such an insignificant thing?
    stubCardCount() {
      const cardMinWidth = 240; // WET. See styles.
      return window.Math.ceil(window.innerWidth / 240);
    },
  },

  methods: {
    getSectionNamespace() {
      return 'products'
    },
  },

  watch: {
    products() {
      this.$emit('_products-updated');
    },
  },

  data() {
    return {
      //
    }
  },
}
</script>

<style lang="less">
.products-list {
  @apply flex flex-wrap;

  .products-list-item {
    flex: 1 1 calc(3 * theme('width.20')); // WET. See JS.

    &--stub {
      @apply h-0 opacity-0;
      @class-name: products-list-item--stub;
      &.@{class-name}.@{class-name} { // Increasing specificity.
        @apply my-0;
      }
    }
  }

  &.cards-style-type-frameless {
    @apply p-0 -mx-2;

    .products-list-item {
      @apply m-2;
    }
  }

  &.cards-wide {
    .products-list-item {
      flex: 1 1 22rem;
    }
  }

  &.cards-style-type-compact,
  &.cards-style-type-long {
    @apply -mx-4;
  }
}
</style>
